import React, { useState } from 'react';
import { Card, CardContent, Typography, TextField, Box, Button, IconButton, Stack, Chip, MenuItem } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { ProblemData } from '@app-types/visit';
import { BugReport, Add, Delete } from '@mui/icons-material';

interface ProblemsCardProps {
  problems: ProblemData[];
  onChange: (problems: ProblemData[]) => void;
  disabled?: boolean;
}

const ProblemsCard: React.FC<ProblemsCardProps> = React.memo(({ problems, onChange, disabled = false }) => {
  const [newProblemDesc, setNewProblemDesc] = useState('');

  const addProblem = () => {
    if (!newProblemDesc.trim()) return;
    
    const newProblem: ProblemData = {
      id: `prob-${Date.now()}`,
      description: newProblemDesc.trim(),
      severity: 'moderate',
      type: 'presumptive'
    };
    onChange([...problems, newProblem]);
    setNewProblemDesc('');
  };

  const updateProblem = (id: string, updates: Partial<ProblemData>) => {
    onChange(problems.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const deleteProblem = (id: string) => {
    onChange(problems.filter(p => p.id !== id));
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BugReport sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6">Probleme / Diagnostice</Typography>
          </Box>
        </Box>
        
        {/* Add new problem */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Adaugă problemă/diagnostic"
            value={newProblemDesc}
            onChange={(e) => setNewProblemDesc(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addProblem();
              }
            }}
            disabled={disabled}
            placeholder="ex: Otită externă bilaterală"
          />
          <Button
            startIcon={<Add />}
            onClick={addProblem}
            disabled={disabled || !newProblemDesc.trim()}
            variant="outlined"
          >
            Adaugă
          </Button>
        </Stack>
        
        {/* Problems list */}
        <Stack spacing={2}>
          {problems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Nu există probleme/diagnostice adăugate. Cel puțin un diagnostic este necesar pentru finalizare.
            </Typography>
          )}
          
          {problems.map((problem) => (
            <Box key={problem.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label="Descriere problemă"
                    value={problem.description}
                    onChange={(e) => updateProblem(problem.id, { description: e.target.value })}
                    disabled={disabled}
                    error={!problem.description}
                  />
                </Grid>
                
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Severitate"
                    value={problem.severity ?? 'moderate'}
                    onChange={(e) => updateProblem(problem.id, { severity: e.target.value as ProblemData['severity'] })}
                    disabled={disabled}
                  >
                    <MenuItem value="mild">Ușoară</MenuItem>
                    <MenuItem value="moderate">Moderată</MenuItem>
                    <MenuItem value="severe">Severă</MenuItem>
                    <MenuItem value="critical">Critică</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Tip"
                    value={problem.type ?? 'presumptive'}
                    onChange={(e) => updateProblem(problem.id, { type: e.target.value as ProblemData['type'] })}
                    disabled={disabled}
                  >
                    <MenuItem value="definitive">Definitiv</MenuItem>
                    <MenuItem value="presumptive">Presumtiv</MenuItem>
                    <MenuItem value="differential">Diferențial</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid size={{ xs: 10, md: 2 }}>
                  <TextField
                    fullWidth
                    label="Cod ICD"
                    value={problem.icdCode ?? ''}
                    onChange={(e) => updateProblem(problem.id, { icdCode: e.target.value })}
                    disabled={disabled}
                    placeholder="optional"
                  />
                </Grid>
                
                <Grid size={{ xs: 2, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    color="error"
                    onClick={() => deleteProblem(problem.id)}
                    disabled={disabled}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
});

ProblemsCard.displayName = 'ProblemsCard';

export default ProblemsCard;

